import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from '@/store/reducers';
import { setProjectCode, setCodeChanged, setEditMode } from '@/store/actions/projectdata.action';
import CommonService from '@/services/common.service';
import { injectCssIntoHtmlHead } from '../utils/content-extractors';

const GrapesJSAutoSaveHandler: React.FC = () => {
    const params = useParams();
    const projectId = params.projectId;
    const dispatch = useDispatch();
    const tempCode = useSelector((state: IRootReducerState) => state.projectData.tempCode);

    useEffect(() => {
        if (!projectId) return;

        // Subscribe to the GrapesJS auto-save event
        const subscription = CommonService._communications.triggerGrapesJSSaveSubject.subscribe(
            async (saveData) => {
                const { htmlContent, cssContent, commitMessage } = saveData;
                
                
                // Create a temporary loading overlay
                const loadingOverlay = document.createElement('div');
                loadingOverlay.style.cssText = `
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    color: white;
                    font-family: 'Inter', sans-serif;
                    border-radius: 8px;
                `;
                
                loadingOverlay.innerHTML = `
                    <div style="
                        background: #1f2937;
                        padding: 30px;
                        border-radius: 12px;
                        border: 1px solid #374151;
                        text-align: center;
                        max-width: 400px;
                        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                    ">
                        <div style="
                            width: 40px;
                            height: 40px;
                            border: 3px solid #374151;
                            border-top: 3px solid #10b981;
                            border-radius: 50%;
                            animation: spin 1s linear infinite;
                            margin: 0 auto 20px;
                        "></div>
                        <h3 style="margin: 0 0 10px; color: #f9fafb; font-size: 18px;">Saving Changes</h3>
                        <p style="margin: 0; color: #d1d5db; font-size: 14px;">Processing visual editor updates...</p>
                        <div style="
                            margin-top: 15px;
                            padding: 10px;
                            background: #374151;
                            border-radius: 6px;
                            font-size: 12px;
                            color: #9ca3af;
                        ">
                            <div>✓ Extracting HTML content</div>
                            <div>✓ Processing CSS styles</div>
                            <div>⏳ Updating project files</div>
                            <div>⏳ Preparing commit</div>
                        </div>
                    </div>
                    <style>
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    </style>
                `;
                
                // Find the preview container and append the overlay to it
                const previewContainer = document.querySelector('.sandpack-html-preview-content') as HTMLElement;
                if (previewContainer) {
                    previewContainer.style.position = 'relative';
                    previewContainer.appendChild(loadingOverlay);
                } else {
                    // Fallback to body if preview container not found
                    document.body.appendChild(loadingOverlay);
                }
                
                try {
                    
                    // Parse current temp code
                    const currentCode = tempCode || '{}';
                    const parsedCode = JSON.parse(currentCode);
                    
                    // Update HTML content (assuming index.html as default)
                    let updatedHtmlContent = '';
                    if (htmlContent) {
                        // Find the current HTML file content
                        const currentHtmlFile = parsedCode['index.html'];
                        const currentHtmlContent = typeof currentHtmlFile === 'string' ? currentHtmlFile : currentHtmlFile?.code || '';
                        
                        // Extract body content from the updated HTML
                        let bodyContent = htmlContent;
                        const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                        if (bodyMatch) {
                            bodyContent = bodyMatch[1];
                        }
                        
                        // Replace only the body content in the original HTML, preserving head and other parts
                        updatedHtmlContent = currentHtmlContent;
                        const originalBodyMatch = currentHtmlContent.match(/<body[^>]*>[\s\S]*?<\/body>/i);
                        
                        if (originalBodyMatch) {
                            // Replace only the body content while preserving the original body tag attributes
                            const originalBodyTag = originalBodyMatch[0];
                            const bodyTagMatch = originalBodyTag.match(/<body[^>]*>/);
                            const originalBodyAttributes = bodyTagMatch ? bodyTagMatch[0] : '<body>';
                            
                            updatedHtmlContent = currentHtmlContent.replace(
                                /<body[^>]*>[\s\S]*?<\/body>/i,
                                `${originalBodyAttributes}${bodyContent}</body>`
                            );
                        } else {
                            // If no body tag exists, check if it's a complete HTML document
                            if (currentHtmlContent.includes('<html') && currentHtmlContent.includes('</html>')) {
                                // Insert body content before closing html tag
                                updatedHtmlContent = currentHtmlContent.replace(
                                    /<\/html>/i,
                                    `<body>${bodyContent}</body></html>`
                                );
                            } else {
                                // If it's not a complete HTML document, wrap the content in body tags
                                updatedHtmlContent = `<body>${bodyContent}</body>`;
                            }
                        }
                        
                        parsedCode['index.html'] = {
                            code: updatedHtmlContent,
                            active: true
                        };
                    }
                    
                    // Update CSS content if provided
                    if (cssContent && cssContent.trim()) {
                        const cssFiles = Object.keys(parsedCode).filter(file => file.endsWith('.css'));
                        const mainCssFile = cssFiles.find(f => f.includes('style') || f.includes('index')) || cssFiles[0];
                        
                        if (mainCssFile) {
                            parsedCode[mainCssFile] = {
                                code: cssContent,
                                active: true
                            };
                        } else {
                            parsedCode['style.css'] = {
                                code: cssContent,
                                active: true
                            };
                        }
                        
                        // Inject CSS into the HTML file to ensure it's properly included
                        if (updatedHtmlContent) {
                            const htmlWithCss = injectCssIntoHtmlHead(updatedHtmlContent, cssContent);
                            parsedCode['index.html'] = {
                                code: htmlWithCss,
                                active: true
                            };
                        }
                    }
                    
                    // Prepare payload for API call
                    const payload = {
                        data: parsedCode,
                        title: commitMessage || "Edited using visual editor",
                    };
                    
                    // Call the API to save the changes
                    await CommonService._project_service.updateCode(projectId, payload);
                    
                    // Update Redux store
                    dispatch(setProjectCode(JSON.stringify(parsedCode)));
                    dispatch(setCodeChanged(false));
                    
                    // Show success message
                    CommonService._alert.showToast("Changes saved successfully", "success");
                    
                    
                    // Show success message briefly
                    setTimeout(() => {
                        loadingOverlay.innerHTML = `
                            <div style="
                                background: #1f2937;
                                padding: 30px;
                                border-radius: 12px;
                                border: 1px solid #10b981;
                                text-align: center;
                                max-width: 400px;
                                box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                            ">
                                <div style="
                                    width: 40px;
                                    height: 40px;
                                    background: #10b981;
                                    border-radius: 50%;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    margin: 0 auto 20px;
                                    font-size: 20px;
                                ">✓</div>
                                <h3 style="margin: 0 0 10px; color: #f9fafb; font-size: 18px;">Save Complete!</h3>
                                <p style="margin: 0; color: #d1d5db; font-size: 14px;">Changes have been automatically saved.</p>
                                <div style="
                                    margin-top: 15px;
                                    padding: 10px;
                                    background: #374151;
                                    border-radius: 6px;
                                    font-size: 12px;
                                    color: #9ca3af;
                                ">
                                    <div>✓ HTML content updated</div>
                                    <div>✓ CSS styles processed</div>
                                    <div>✓ Project files updated</div>
                                    <div>✓ Ready for commit</div>
                                </div>
                            </div>
                        `;
                        
                        // Remove loading overlay after 2 seconds
                        setTimeout(() => {
                            if (loadingOverlay.parentNode) {
                                loadingOverlay.parentNode.removeChild(loadingOverlay);
                            }
                        }, 2000);
                    }, 1000);

                    dispatch(setEditMode(false));

                } catch (error: any) {
                    console.error('Error in auto-save:', error);
                    CommonService._alert.showToast("Error saving changes", "error");
                    
                    // Show error message
                    loadingOverlay.innerHTML = `
                        <div style="
                            background: #1f2937;
                            padding: 30px;
                            border-radius: 12px;
                            border: 1px solid #ef4444;
                            text-align: center;
                            max-width: 400px;
                            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                        ">
                            <div style="
                                width: 40px;
                                height: 40px;
                                background: #ef4444;
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                margin: 0 auto 20px;
                                font-size: 20px;
                            ">✕</div>
                            <h3 style="margin: 0 0 10px; color: #f9fafb; font-size: 18px;">Auto-Save Failed</h3>
                            <p style="margin: 0; color: #d1d5db; font-size: 14px;">There was an error Saving your changes.</p>
                            <button onclick="this.parentElement.parentElement.parentElement.remove()" style="
                                margin-top: 15px;
                                padding: 8px 16px;
                                background: #ef4444;
                                color: white;
                                border: none;
                                border-radius: 6px;
                                cursor: pointer;
                            ">Close</button>
                        </div>
                    `;
                }
            }
        );

        // Cleanup subscription on unmount
        return () => {
            subscription.unsubscribe();
        };
    }, [projectId, tempCode, dispatch]);

    // This component doesn't render anything
    return null;
};

export default GrapesJSAutoSaveHandler; 